import ISearchedPosition from 'api/interfaces/offers/ISearchedPosition';
import getContributionMarginPerUnit from 'services/products/calculations/getContributionMarginPerUnit';
import getExpensePerUnit from 'services/products/calculations/getExpensePerUnit';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';

/*
 * Reference prices of a position: the positions of other offers with the same name. They
 * are compared per unit of the position (e.g. €/m²), so only positions with the same unit
 * are compared. The margin % drives the order: the highest margins are the best and the
 * lowest the worst; between them is the median of each figure.
 */

// How many of the best and of the worst prices are shown
export const REFERENCE_COUNT = 5;

export interface IReferenceFigures {
  contributionMargin: number;
  contributionMarginRatio: number;
  expense: number;
  quantity: number;
  sellingPrice: number;
}

export interface IReferenceRow extends IReferenceFigures {
  customerName: string | null;
  offerDate: string;
  offerProductId: number;
  // E.g. approved (ordered), open, rejected (lost)
  offerStatus: string;
  offerType: string;
  organizationUnit: string | null;
  targetName: string;
}

export interface IReferencePrices {
  best: IReferenceRow[];
  count: number;
  median: IReferenceFigures | null;
  worst: IReferenceRow[];
}

const getMedian = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
};

/** The figures per unit of the primary version of each priced position with the unit */
export const getReferenceRows = (
  positions: ISearchedPosition[],
  unit: string
): IReferenceRow[] =>
  positions
    .filter((position) => !unit || position.unit === unit)
    .map((position) => {
      const primaryVersion = position.versions.find(
        (version) => version.isPrimary
      );
      const installations = primaryVersion?.installations ?? [];
      const subproducts = primaryVersion?.subproducts ?? [];

      const sellingPrice = getSellingPricePerUnit(installations, subproducts);
      const contributionMargin = getContributionMarginPerUnit(
        installations,
        subproducts
      );

      return {
        contributionMargin,
        contributionMarginRatio:
          sellingPrice === 0 ? 0 : (contributionMargin / sellingPrice) * 100,
        customerName: position.customerName,
        expense: getExpensePerUnit(installations, subproducts),
        offerDate: position.offerDate,
        offerProductId: position.offerProductId,
        offerStatus: position.offerStatus,
        offerType: position.offerType,
        organizationUnit: position.organizationUnit,
        quantity: position.quantity ?? 0,
        sellingPrice,
        targetName: position.targetName,
      };
    })
    .filter((row) => row.sellingPrice > 0)
    // The same margin % in order of the selling price
    .sort(
      (a, b) =>
        b.contributionMarginRatio - a.contributionMarginRatio ||
        b.sellingPrice - a.sellingPrice
    );

/**
 * The best and the worst prices and the median of all prices. With fewer than ten prices
 * they are divided between the best and the worst, so no price is shown twice.
 */
export const getReferencePrices = (rows: IReferenceRow[]): IReferencePrices => {
  const bestCount = Math.min(REFERENCE_COUNT, Math.ceil(rows.length / 2));
  const best = rows.slice(0, bestCount);
  const worst = rows.slice(Math.max(bestCount, rows.length - REFERENCE_COUNT));

  if (rows.length === 0) {
    return { best, count: 0, median: null, worst };
  }

  // The median selling price and expense; the margin of the row is calculated from them,
  // so the row adds up like the other rows
  const sellingPrice = getMedian(rows.map((row) => row.sellingPrice));
  const expense = getMedian(rows.map((row) => row.expense));
  const contributionMargin = sellingPrice - expense;

  const median = {
    contributionMargin,
    contributionMarginRatio:
      sellingPrice === 0 ? 0 : (contributionMargin / sellingPrice) * 100,
    expense,
    quantity: getMedian(rows.map((row) => row.quantity)),
    sellingPrice,
  };

  return { best, count: rows.length, median, worst };
};
